import { FormattedTitle } from "@components/atoms/FormattedTitle"
import { ParallaxCarousel, ParallaxCarouselType } from "@components/media/ParallaxCarousel"
import { Gradient, GradientType } from "@components/motion/Gradient"
import { Button } from "@components/ui/Button"
import { cn } from "@utils/tw"
import { ContentBlocks, defineBlock, InnerBlocks } from "eddev/blocks"
import { Fragment, useEffect, useRef, useState } from "react"
import { useRafLoop } from "react-use"
import { ResponsiveImageFragment } from "../../types.graphql"
import { motion } from "motion/react"
import { IconPlay } from "@components/ui/Icon"
import Marquee from "react-fast-marquee"
import { Link } from "eddev/routing"
import { useIsMobile } from "@hooks/useIsMobile"
import { PlayTrailerButton } from "@components/media/TrailerOverlay"

export const meta: BlockMeta = {
  title: "Homepage Carousel",
  inserter: true,
  tags: ["homepage-root"],
  flags: {
    fullWidth: true,
  },
}

export default defineBlock("homepage/homepage-carousel", (props) => {
  const gradientRef = useRef<GradientType>(null)
  const introCarouselRef = useRef<ParallaxCarouselType>(null)
  const carouselRef = useRef<ParallaxCarouselType>(null)
  const [ready, setReady] = useState(false)
  const [theme, setTheme] = useState<string | undefined>(undefined)
  const [current, setCurrent] = useState(0)
  const intro = useRef(-2)
  const isMobile = useIsMobile()

  const allImages =
    props.slides
      ?.map((slide) => {
        if (slide?.film) {
          const im = slide.film.media?.gallery?.[0]
          return { aspect: im?.aspect ?? 1, srcSet: im?.srcSet || "" }
        }
        if (slide?.page) {
          const header = slide.page.contentBlocks.find((b) => b.blockName === "acf/content-page-header")
          const props = header?.props as { featuredImage: ResponsiveImageFragment }
          if (props.featuredImage) {
            return {
              aspect: props.featuredImage.mediaDetails?.width! / props.featuredImage.mediaDetails?.height!,
              srcSet: props.featuredImage.srcSet || "",
            }
          }
        }
        if (slide?.image) {
          return {
            aspect: slide.image.mediaDetails?.width! / slide.image.mediaDetails?.height!,
            srcSet: slide.image.srcSet || "",
          }
        }
        return ""
      })
      .filter((i) => i !== "") ?? []
  const allThemes =
    props.slides
      ?.map((slide) => {
        if (slide?.page) {
          const pageHeader = slide.page.contentBlocks.find((b) => b.blockName === "acf/content-page-header")
          return (pageHeader?.props as { themed: string }).themed
        }
        if (slide?.theme) {
          return slide.theme
        }
        return "yellow"
      })
      .filter(Boolean) ?? []

  useRafLoop((time) => {
    const isMobile = window.innerWidth < 900
    const OFFSET = isMobile ? 0 : 0.45
    intro.current += (1 - intro.current) * 0.075
    introCarouselRef.current?.setOffsetXPercent(intro.current * OFFSET)
    introCarouselRef.current?.setOverallOpacity(1 - intro.current)
    carouselRef.current?.setOffsetXPercent(intro.current * OFFSET)
    // carouselRef.current?.setOverallOpacity(ready ? 1 : 0)
    if (intro.current > 0.95) {
      setReady(true)
    }
  })

  useEffect(() => {
    gradientRef.current?.resize()
  }, [current, ready, isMobile])

  return (
    <div className="w-full">
      <div className="py-4 relative">
        <Gradient
          ref={gradientRef}
          theme={allThemes[current]}
          className="absolute left-0 top-0 h-full"
          aspect="1/1"
          showImage={false}
          cutEdges={false}
          align="left"
        />
        <div className="w-full relative">
          {/* <ParallaxCarousel
          ref={introCarouselRef}
          className={cn("h-[35vw] max-h-[45vh] absolute left-0 top-0", ready && "pointer-events-none")}
          images={[allImages[0], allImages[0], allImages[0], ...allImages]}
          inactiveWidth={1}
          startIndex={2}
          fadePreviousItems={ready}
          controls={false}
        /> */}
          <ParallaxCarousel
            ref={carouselRef}
            className={cn("h-homeCarouselH", !ready && "pointer-events-none")}
            images={allImages}
            fadePreviousItems={ready && !isMobile}
            onChange={(index) => {
              setCurrent(index)
            }}
            clampToRightEdge={isMobile}
          />
        </div>
        <div className="relative pt-8 md:pt-0 pb-4 md:pb-0 px-4 md:px-0 md:absolute w-full md:w-[45%] md:h-full md:left-0 md:top-0 overflow-hidden md:overflow-visible">
          {props.slides?.map((slide, index) => {
            const pageHeader = slide?.page?.contentBlocks.find((b) => b.blockName === "acf/content-page-header")
            const pageHeaderProps = pageHeader?.props as { featuredText: string; themed: string }

            return (
              (ready || isMobile) &&
              index === current && (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="md:absolute md:left-0 md:top-0 md:px-8 md:h-full flex flex-col items-center md:items-start justify-center w-full"
                >
                  {slide?.page && (
                    <div className="w-full md:w-auto flex flex-col">
                      <FormattedTitle
                        text={
                          slide.featuredText && slide.featuredText !== ""
                            ? slide.featuredText
                            : pageHeaderProps.featuredText
                        }
                        theme={pageHeaderProps.themed}
                      />
                      <div className="flex flex-col gap-3 mt-8 md:mt-6">
                        <p className="type-label-m md:type-label-s">{slide.page.title}</p>
                        <div className="[&_p]:type-body-l [&_p]:md:type-body-m md:w-3/4">
                          <ContentBlocks blocks={pageHeader?.innerBlocks} />
                        </div>
                        <Button href={slide.page.uri} className="w-fit">
                          {slide.page.title}
                        </Button>
                      </div>
                    </div>
                  )}
                  {slide?.featuredText && slide?.featuredText !== "" && !slide.page && (
                    <FormattedTitle text={slide.featuredText} theme={allThemes[current]} />
                  )}
                  {slide?.film && (
                    <div className="w-full md:w-auto flex flex-col">
                      <div className="flex flex-col gap-3 mt-8 md:mt-6">
                        <p className="type-label-m md:type-label-s">{slide.film.title}</p>
                        {slide.film.excerpt && (
                          <div
                            className="type-body-l [&_p]:type-body-l md:type-body-m md:[&_p]:type-body-m md:w-3/4 line-clamp-4"
                            dangerouslySetInnerHTML={{ __html: slide.film.excerpt }}
                          />
                        )}
                        <div className="flex gap-4">
                          <Button href={slide.film.uri} className="w-fit">
                            Book Tickets
                          </Button>
                          {slide.film.eventInfo?.trailer && (
                            <PlayTrailerButton videoId={slide.film.eventInfo.trailer}>
                              <Button
                                variant="outline"
                                className="w-fit border-[2px] bg-transparent pl-3"
                                leftIcon={<IconPlay className="!w-[1.25em] !h-[1.25em]" />}
                              >
                                Trailer
                              </Button>
                            </PlayTrailerButton>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {slide?.type === "image" && ((slide.bodyText && slide?.bodyText !== "") || slide.bodyButton?.url) && (
                    <div className="flex w-full flex-col gap-3 mt-8 md:mt-6">
                      {slide.bodyText && (
                        <div className="[&_p]:type-body-l [&_p]:md:type-body-m md:w-3/4">{slide.bodyText}</div>
                      )}
                      {slide.bodyButton?.url && (
                        <Button href={slide.bodyButton.url} target={slide.bodyButton.target || ""} className="w-fit">
                          {slide.bodyButton.title || "Learn More"}
                        </Button>
                      )}
                    </div>
                  )}
                </motion.div>
              )
            )
          })}
        </div>
      </div>
      {props.ticker && props.ticker.length > 0 && (
        <div className="w-full h-8 bg-black text-white flex items-center justify-center">
          <Marquee autoFill>
            <div className="flex gap-4 items-center mr-4 type-label-m">
              {props.ticker?.map((item, index) => (
                <Fragment key={index}>
                  <Link href={item?.link?.url} target={item?.link?.target || undefined} className="mt-[0.2em]">
                    {item?.text}
                  </Link>
                  <span className="w-2 h-2 bg-white block" />
                </Fragment>
              ))}
            </div>
          </Marquee>
        </div>
      )}
    </div>
  )
})
